import React from 'react';
import { Link } from 'react-router-dom';
import SEO, { SITE_URL } from './SEO';
import Footer from './Footer';
import { clusters, UNVERIFIED, RESEARCH_UPDATED, totalStats } from './researchData';
import './PricingPage.css';
import './RateCard.css';
import './ResearchPage.css';

const schema = [
  {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Missed-Call and AI Receptionist Statistics, With Sources',
    description:
      `${totalStats} verified statistics on missed calls, speed-to-lead, receptionist costs, and AI adoption — each with its original source, date, and sample size. Plus the widely cited figures we could not verify.`,
    datePublished: '2026-08-14',
    dateModified: '2026-08-14',
    author: {
      '@type': 'Organization',
      name: 'UBOTIKA',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: 'UBOTIKA',
      url: SITE_URL,
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/research` },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Research', item: `${SITE_URL}/research` },
    ],
  },
];

function ResearchPage() {
  return (
    <>
      <SEO
        title={`Missed-Call Statistics (${totalStats} Sourced Figures) | UBOTIKA Research`}
        description="Verified statistics on missed calls, speed-to-lead, receptionist costs in Los Angeles, bilingual demand, and AI adoption — each with its original source, year, and sample size. Includes the widely quoted figures that have no traceable source."
        path="/research"
        schema={schema}
      />
      <main id="main-content" className="pricing-page ratecard-page research-page">
        <section className="pricing-hero">
          <h1>Missed-call statistics, with the receipts.</h1>
          <p className="pricing-sub">
            {totalStats} figures on missed calls, response speed, what a receptionist really
            costs in Los Angeles, and how people actually feel about AI answering the phone —
            each one carrying its original source, year, and sample size. Free to cite.
          </p>
        </section>

        <section className="ratecard-section">
          <div className="research-method">
            <h2>How this page was made</h2>
            <p>
              Most statistics in this industry are laundered: a vendor blog quotes a vendor
              blog, the year quietly updates, and the sample size disappears. Every figure
              below was traced back to a primary source we actually read. Where the trail
              ended, we say so instead of publishing the number —{' '}
              <a href="#unverified">six commonly cited claims failed that test</a> and are
              listed at the bottom.
            </p>
            <p className="ratecard-fine" style={{ marginTop: 12 }}>
              Last verified {RESEARCH_UPDATED}. Found an error, or a primary source for
              something we marked unverified?{' '}
              <a href="mailto:hello@ubotika.com?subject=Research%20page%20correction">
                Tell us and we&apos;ll correct it
              </a>
              .
            </p>
          </div>
        </section>

        {clusters.map((c) => (
          <section className="ratecard-section" id={c.id} key={c.id}>
            <h2>
              {c.title}
              {c.la && <span className="research-tag">Los Angeles</span>}
            </h2>
            <p className="ratecard-lede">{c.lede}</p>
            <div className="research-list">
              {c.stats.map((s) => (
                <article
                  className={`research-stat${s.against ? ' research-stat--against' : ''}${s.ours ? ' research-stat--ours' : ''}`}
                  key={s.claim}
                >
                  <div className="research-figure">{s.figure}</div>
                  <div className="research-body">
                    <p className="research-claim">{s.claim}</p>
                    {s.detail && <p className="research-detail">{s.detail}</p>}
                    <p className="research-source">
                      {s.ours ? 'Our arithmetic — ' : 'Source: '}
                      <a href={s.url} target="_blank" rel="noopener noreferrer">
                        {s.source}
                      </a>
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </section>
        ))}

        <section className="ratecard-section" id="unverified">
          <h2>Statistics we could not verify</h2>
          <p className="ratecard-lede">
            Every claim below appears on competitors&apos; pages as established fact. We went
            looking for the original study behind each one and could not find it. We are not
            saying they are false — we are saying nobody can show you where they came from,
            so we won&apos;t repeat them.
          </p>
          <div className="research-unverified">
            {UNVERIFIED.map((u) => (
              <article className="research-myth" key={u.claim}>
                <h3>{u.claim}</h3>
                <p>{u.finding}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="ratecard-pilot">
          <h2>What your own numbers look like</h2>
          <p>
            Industry averages only get you so far. A free audit tells you how many calls
            your business is actually missing, when they arrive, and what they&apos;re worth —
            using your data, not a 2015 study of somebody else.
          </p>
          <Link to="/free-missed-lead-audit" className="cta-button">Get a free audit</Link>
        </section>

        <section className="ratecard-section">
          <p className="ratecard-fine">
            Citing this page? Link to <a href={`${SITE_URL}/research`}>{SITE_URL}/research</a>{' '}
            and please cite the original source alongside us — every one is linked above.
            Related: <Link to="/compare">how UBOTIKA compares to alternatives</Link> ·{' '}
            <Link to="/faq">every question we get, answered</Link>
          </p>
        </section>
        <Footer />
      </main>
    </>
  );
}

export default ResearchPage;
